import React from "react";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import { useHistory, useParams } from "react-router-dom";

import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { isWithinInterval, startOfDay, subDays } from "date-fns";

import { TIME_BLOCKS_TO_EXCLUDE } from "~/calendar/utils/types";
import { AppContext } from "~/common";
import { useMedia } from "~/lib/hooks";
import { useMutationPromise } from "~/lib/relayUtil";
import { useGroupEmitter } from "~/lib/websocket";
import { StreamSideBarContext } from "~/stream/SideBar/SideBarContext";
import type { URLParams } from "~/stream/StreamHeader/StreamHeader";
import { baseStreamUrl } from "~/stream/utils";
import Menu from "~/ui/menu";

import { type AbstractAction, ActionButton } from "./shared";

import type { MeetingBriefs_useShowMeetingBriefSuggestedAction$key } from "~/__generated__/MeetingBriefs_useShowMeetingBriefSuggestedAction.graphql";
import type { MeetingBriefsCreateMeetingBriefMutation } from "~/__generated__/MeetingBriefsCreateMeetingBriefMutation.graphql";
import type { MeetingBriefsSuggestedAction_note$key } from "~/__generated__/MeetingBriefsSuggestedAction_note.graphql";

const MEETING_BRIEF_ACTION_ID = "meeting-brief" as const;

export const briefMe = {
	id: MEETING_BRIEF_ACTION_ID,
	button: MeetingBriefButton,
	menuItem: MeetingBriefMenuItem,
} satisfies AbstractAction;

export function useShowMeetingBriefSuggestedAction(noteKey: MeetingBriefs_useShowMeetingBriefSuggestedAction$key) {
	const [note, refetch] = useRefetchableFragment(
		graphql`
			fragment MeetingBriefs_useShowMeetingBriefSuggestedAction on Note
			@refetchable(queryName: "MeetingBriefsShowMeetingBriefSuggestedActionQuery") {
				event {
					timeBlockType
					attendeePersonCount
					externalAttendeePersonCount
				}
				eventStart
				eventEnd
				groupName
				previousNoteWithEvent {
					meetingRecordings {
						id
					}
				}
				meetingBrief {
					id
				}
			}
		`,
		noteKey,
	);

	const { account } = React.useContext(AppContext);
	const featureIsOn = useFeatureIsOn("meeting-briefs");
	const includeInternalFolks = useFeatureIsOn("meeting-brief-internal-attendees");
	const countedAttendees =
		(includeInternalFolks ? (note.event?.attendeePersonCount || 1) - 1 : note.event?.externalAttendeePersonCount) || 0;

	const groupEmitter = useGroupEmitter(note.groupName);
	React.useEffect(() => {
		function onMessage(message: { type: string }) {
			if (message.type === "MEETING_BRIEF_READY") {
				refetch({});
			}
		}

		groupEmitter.on("message", onMessage);
		return () => groupEmitter.off("message", onMessage);
	}, [groupEmitter, refetch]);

	return (
		// Feature flag and billing feature must be enabled
		!!account?.allowAiFeatures &&
		account.billingAccount.hasAccessToMeetingBriefs &&
		!account.billingAccount.product.legacy &&
		featureIsOn &&
		// Note must not already have a brief
		!note.meetingBrief?.id &&
		// Note must have an event (not OOO or focus time)
		note.event &&
		!TIME_BLOCKS_TO_EXCLUDE.includes(note.event.timeBlockType || "") &&
		// Event must have more than one attendee and fewer than 9 OR the previous note with an event has a recording
		((countedAttendees > 1 && countedAttendees < 9) ||
			(note.previousNoteWithEvent?.meetingRecordings?.length || 0) > 0) &&
		// Event must not be in the past
		// Show it for some amount of time before the meeting starts
		!!note.eventStart &&
		!!note.eventEnd &&
		isWithinInterval(new Date(), {
			start: startOfDay(subDays(new Date(note.eventStart), 3)), // 3 days before the meeting starts (at the start of that day)
			end: new Date(note.eventEnd), // The end of the meeting
		})
	);
}

function MeetingBriefButton({ note: noteKey }: { note: MeetingBriefsSuggestedAction_note$key }) {
	const [onClick, isInFlight] = useBriefMe(noteKey);

	return (
		<ActionButton actionId={MEETING_BRIEF_ACTION_ID} onClick={onClick} disabled={isInFlight}>
			<FontAwesomeIcon icon={faSparkles} color="var(--vividAI)" style={{ marginRight: "0.25rem" }} />
			Brief me
		</ActionButton>
	);
}

function MeetingBriefMenuItem({ note: noteKey }: { note: MeetingBriefsSuggestedAction_note$key }) {
	const [onClick, isInFlight] = useBriefMe(noteKey);

	return (
		<Menu.Item icon={faSparkles} iconProps={{ color: "var(--vividAI)" }} onSelect={onClick} disabled={isInFlight}>
			Brief me
		</Menu.Item>
	);
}

export function useBriefMe(noteKey: MeetingBriefsSuggestedAction_note$key) {
	const note = useFragment(
		graphql`
			fragment MeetingBriefsSuggestedAction_note on Note {
				id
				meetingBrief {
					id
				}
			}
		`,
		noteKey,
	);

	const [createMeetingBrief, isInFlight] = useCreateMeetingBriefMutation(note.id);
	const sideBarContext = React.useContext(StreamSideBarContext);

	const history = useHistory();
	const routeParams = useParams<URLParams>();
	const baseURL = React.useMemo(
		() => baseStreamUrl(routeParams, window.INITIAL_STATE.user?.type === "GUEST"),
		[routeParams],
	);

	// NOTE: must match logic used in TabsAndBookmarks to show sidebar stuff as tabs
	const isSmallScreen = useMedia("(max-width: 734px)");

	const onClick = React.useCallback(async () => {
		const result = await createMeetingBrief();
		const noteId = result.createMeetingBrief?.note.id;
		if (isSmallScreen) {
			history.push({ pathname: `${baseURL}meeting-briefs/`, state: { label: "Ask Fellow" } });
		} else if (noteId) {
			sideBarContext.setMeetingBriefContext({ noteId });
		}
	}, [sideBarContext, createMeetingBrief, isSmallScreen, baseURL, history]);

	return [onClick, isInFlight] as const;
}

function useCreateMeetingBriefMutation(noteId: string) {
	const [commit, isInFlight] = useMutationPromise<MeetingBriefsCreateMeetingBriefMutation>(graphql`
		mutation MeetingBriefsCreateMeetingBriefMutation($noteId: ID!) {
			createMeetingBrief(noteId: $noteId) {
				note {
					id
					meetingBrief {
						id
					}
				}
			}
		}
	`);

	const createMeetingBrief = React.useCallback(async () => {
		return await commit({ variables: { noteId } });
	}, [commit, noteId]);

	return [createMeetingBrief, isInFlight] as const;
}
